import { useNavigate, type Link as BaseLink } from '@exam-management/router';
import { useNavigationHistory } from '@shared/navigation';
import { type ComponentProps, useCallback, useMemo } from 'react';
import { matchPath } from 'react-router-dom';

type BaseLinkProps = ComponentProps<typeof BaseLink>;

interface UseNavigateBackProps {
  fallbackPath: BaseLinkProps['to'];
  matchPatterns?: string | string[];
  fallbackParams?: BaseLinkProps['params'];
  replace?: BaseLinkProps['replace'];
}

/**
 *
 * @param previousPath The previous path from navigation history
 * @param patterns The patterns to match against if patterns is not provided will check if previousPath exists and navigate back if it does, otherwise navigate to fallbackPath
 * @returns boolean indicating whether we can navigate back to the previous path based on the provided patterns
 */
function canGoBack(previousPath: string, patterns?: string[]): boolean {
  if (!patterns) {
    return true;
  }

  return matchesPattern(previousPath, patterns);
}

function matchesPattern(pathname: string, patterns: string[]): boolean {
  return patterns.some((pattern) => matchPath(pattern, pathname) !== null);
}

export function useNavigateBack({
  fallbackPath,
  matchPatterns,
  fallbackParams,
  replace = false,
}: UseNavigateBackProps) {
  const navigate = useNavigate();
  const { previousPath } = useNavigationHistory();

  const patterns = useMemo(() => {
    if (!matchPatterns) {
      return undefined;
    }
    return Array.isArray(matchPatterns) ? matchPatterns : [matchPatterns];
  }, [matchPatterns]);

  const handleBack = useCallback(() => {
    if (previousPath && canGoBack(previousPath, patterns)) {
      navigate(-1);
    } else if (fallbackParams) {
      // cast to any to satisfy navigate overloads when params is a union type
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate(fallbackPath, { params: fallbackParams, replace } as any);
    } else {
      navigate(fallbackPath, { replace });
    }
  }, [navigate, previousPath, fallbackPath, fallbackParams, replace, patterns]);

  return { handleBack };
}
